import { useState, useEffect, useRef, useCallback } from "react";
import { collection, query, orderBy, where, onSnapshot, getCountFromServer, getDocs } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, User, MessageCircle } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";

// ADC-IMPLEMENTS: <adc-006a-feature-convlist-01>
export interface Conversation {
  id: string;
  jid: string;
  type: 'dm' | 'group';
  name: string;
  lastMessageTimestamp: number;
  lastMessagePreview?: string;
  flaggedCount: number;
  totalFlaggedCount?: number;
  photoUrl?: string;
  participants?: {
    id: string;
    name?: string;
  }[];
  lastFlaggedReviewAt?: Date;
  // ADC-006A v2.0.0: activeIncidentCount removed -- incidents are browsed from IncidentDashboard
}

const extractPhoneFromJid = (jid: string): string => {
  if (jid.includes('@g.us')) {
    return jid.split('@')[0];
  }
  
  const match = jid.match(/^(\d+)@/);
  return match ? match[1] : jid;
};

const getDocumentCount = async (
  collectionPath: string, 
  whereConditions?: Array<[string, any, any]>, 
  includeTimestampFilter: boolean = true
): Promise<number> => {
  try {
    console.log(`Getting count for collection: ${collectionPath}`);
    console.log(`With where conditions:`, whereConditions);
    console.log(`Including timestamp filter: ${includeTimestampFilter}`);
    
    const collectionRef = collection(db, collectionPath);
    let countQuery = query(collectionRef);
    
    if (whereConditions && whereConditions.length > 0) {
      const filteredConditions = includeTimestampFilter 
        ? whereConditions 
        : whereConditions.filter(condition => condition[0] !== "messageTimestamp");
      
      if (filteredConditions.length > 0) {
        countQuery = query(collectionRef, where(filteredConditions[0][0], filteredConditions[0][1], filteredConditions[0][2]));
        
        for (let i = 1; i < filteredConditions.length; i++) {
          countQuery = query(countQuery, where(filteredConditions[i][0], filteredConditions[i][1], filteredConditions[i][2]));
        }
      }
    }
    
    const countSnapshot = await getCountFromServer(countQuery);
    const count = countSnapshot.data().count;
    console.log(`Count result for ${collectionPath}: ${count}`);
    return count;
  } catch (err) {
    console.error(`Error getting count for ${collectionPath}:`, err);
    
    try {
      console.log(`Trying fallback approach for ${collectionPath}`);
      const collectionRef = collection(db, collectionPath);
      let docsQuery = query(collectionRef);
      
      if (whereConditions && whereConditions.length > 0) {
        const filteredConditions = includeTimestampFilter 
          ? whereConditions 
          : whereConditions.filter(condition => condition[0] !== "messageTimestamp");
        
        if (filteredConditions.length > 0) {
          docsQuery = query(collectionRef, where(filteredConditions[0][0], filteredConditions[0][1], filteredConditions[0][2]));
          
          for (let i = 1; i < filteredConditions.length; i++) {
            docsQuery = query(docsQuery, where(filteredConditions[i][0], filteredConditions[i][1], filteredConditions[i][2]));
          }
        }
      }
      
      const docsSnapshot = await getDocs(docsQuery);
      console.log(`Fallback count for ${collectionPath}: ${docsSnapshot.size}`);
      return docsSnapshot.size;
    } catch (fallbackErr) {
      console.error(`Fallback approach also failed for ${collectionPath}:`, fallbackErr);
      return 0;
    }
  }
};

interface ConversationListProps {
  connectionId: string;
  onSelectConversation: (conversation: Conversation) => void;
  selectedConversationId?: string;
  className?: string;
}

export function ConversationList({ 
  connectionId, 
  onSelectConversation, 
  selectedConversationId,
  className = ""
}: ConversationListProps) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flaggedCounts, setFlaggedCounts] = useState<Record<string, number>>({});
  const [totalFlaggedCounts, setTotalFlaggedCounts] = useState<Record<string, number>>({});
  const conversationCategoryRef = useRef<Record<string, 'new' | 'archive'>>({});
  const hasInitialLoadCompletedRef = useRef(false);
  
  const conversationsUnsubscribeRef = useRef<(() => void) | null>(null);
  
  useEffect(() => {
    if (!connectionId) return;
    
    setLoading(true);
    setError(null);
    hasInitialLoadCompletedRef.current = false;
    
    if (conversationsUnsubscribeRef.current) {
      conversationsUnsubscribeRef.current();
      conversationsUnsubscribeRef.current = null;
    }
    
    const conversationsRef = collection(db, `connections/${connectionId}/conversations`);
    const conversationsQuery = query(
      conversationsRef, 
      orderBy("lastMessageTimestamp", "desc")
    );
    
    try {
      const unsubscribe = onSnapshot(
        conversationsQuery,
        async (snapshot) => {
          try {
            if (snapshot.empty) {
              setConversations([]);
              setLoading(false);
              hasInitialLoadCompletedRef.current = true;
              return;
            }
            
            const conversationsData: Conversation[] = [];
            const newFlaggedCounts: Record<string, number> = {...flaggedCounts};
            const newTotalFlaggedCounts: Record<string, number> = {...totalFlaggedCounts};
            const newCategories: Record<string, 'new' | 'archive'> = {...conversationCategoryRef.current};
            
            const conversationPromises = snapshot.docs.map(async (doc) => {
              const data = doc.data();
              const conversationId = doc.id;
              
              const messagesPath = `connections/${connectionId}/conversations/${conversationId}/messages`;
              
              try {
                const lastFlaggedReviewAt = data.lastFlaggedReviewAt ? data.lastFlaggedReviewAt.toDate() : null;
                
                const queryConditions: Array<[string, any, any]> = [
                  ["isFlaggedMessage", "==", true]
                ];
                
                if (lastFlaggedReviewAt) {
                  queryConditions.push(["messageTimestamp", ">", Math.floor(lastFlaggedReviewAt.getTime() / 1000)]);
                }
                
                const flaggedCount = await getDocumentCount(messagesPath, queryConditions, true);
                const totalFlaggedCount = await getDocumentCount(messagesPath, queryConditions, false);

                console.log(`Conversation ${conversationId} has ${flaggedCount} unreviewed flagged messages and ${totalFlaggedCount} total flagged messages`);
                
                newFlaggedCounts[conversationId] = flaggedCount;
                newTotalFlaggedCounts[conversationId] = totalFlaggedCount;
                
                if (!hasInitialLoadCompletedRef.current || !newCategories[conversationId]) {
                  newCategories[conversationId] = flaggedCount > 0 ? 'new' : 'archive';
                } else if (conversationId !== selectedConversationId) {
                  if (flaggedCount > 0 && newCategories[conversationId] === 'archive') {
                    newCategories[conversationId] = 'new';
                  } else if (flaggedCount === 0 && newCategories[conversationId] === 'new') {
                    newCategories[conversationId] = 'archive';
                  }
                }
                
                return {
                  id: conversationId,
                  jid: data.jid || conversationId,
                  type: data.type || 'dm',
                  name: data.name || "Unknown",
                  lastMessageTimestamp: data.lastMessageTimestamp || 0,
                  lastMessagePreview: data.lastMessagePreview || "",
                  flaggedCount: flaggedCount,
                  totalFlaggedCount: totalFlaggedCount,
                  photoUrl: data.photoUrl,
                  participants: data.participants || [],
                  lastFlaggedReviewAt: lastFlaggedReviewAt || undefined,
                };
              } catch (err) {
                console.error(`Error counting flagged messages for conversation ${conversationId}:`, err);
                
                if (!newCategories[conversationId]) {
                  newCategories[conversationId] = 'archive';
                }
                
                return {
                  id: conversationId,
                  jid: data.jid || conversationId,
                  type: data.type || 'dm',
                  name: data.name || "Unknown",
                  lastMessageTimestamp: data.lastMessageTimestamp || 0,
                  lastMessagePreview: data.lastMessagePreview || "",
                  flaggedCount: 0,
                  totalFlaggedCount: 0,
                  photoUrl: data.photoUrl,
                  participants: data.participants || [],
                  lastFlaggedReviewAt: data.lastFlaggedReviewAt ? data.lastFlaggedReviewAt.toDate() : undefined
                };
              }
            });
            
            const resolvedConversations = await Promise.all(conversationPromises);
            conversationsData.push(...resolvedConversations);
            
            conversationsData.sort((a, b) => b.lastMessageTimestamp - a.lastMessageTimestamp);
            
            conversationCategoryRef.current = newCategories;
            hasInitialLoadCompletedRef.current = true;
            
            setFlaggedCounts(newFlaggedCounts);
            setTotalFlaggedCounts(newTotalFlaggedCounts);
            setConversations(conversationsData);
            setLoading(false);
          } catch (err) {
            console.error("Error processing conversations:", err);
            setError("Failed to load conversations");
            setLoading(false);
            hasInitialLoadCompletedRef.current = true;
          }
        },
        (err) => {
          console.error("Error in conversations listener:", err);
          setError(`Failed to listen for conversations: ${err.message}`);
          setLoading(false);
          hasInitialLoadCompletedRef.current = true;
        }
      );
      
      conversationsUnsubscribeRef.current = unsubscribe;
    } catch (error) {
      console.error("Error setting up conversations listener:", error);
      setError("Failed to set up conversations listener");
      setLoading(false);
      hasInitialLoadCompletedRef.current = true;
    }
    
    return () => {
      if (conversationsUnsubscribeRef.current) {
        conversationsUnsubscribeRef.current();
        conversationsUnsubscribeRef.current = null;
      }
    };
  }, [connectionId]);

  const refreshFlaggedCounts = useCallback(async () => {
    if (!connectionId || conversations.length === 0 || loading) return;
    
    const newFlaggedCounts: Record<string, number> = {...flaggedCounts};
    const newTotalFlaggedCounts: Record<string, number> = {...totalFlaggedCounts};
    const newCategories: Record<string, 'new' | 'archive'> = {...conversationCategoryRef.current};
    let hasChanges = false;
    
    for (const conversation of conversations) {
      if (conversation.id === selectedConversationId) continue;
      
      const messagesPath = `connections/${connectionId}/conversations/${conversation.id}/messages`;
      
      const queryConditions: Array<[string, any, any]> = [
        ["isFlaggedMessage", "==", true]
      ];
      
      if (conversation.lastFlaggedReviewAt) {
        queryConditions.push(["messageTimestamp", ">", Math.floor(conversation.lastFlaggedReviewAt.getTime() / 1000)]);
      }
      
      const flaggedCount = await getDocumentCount(messagesPath, queryConditions, true);
      const totalFlaggedCount = await getDocumentCount(messagesPath, queryConditions, false);
      
      if (newFlaggedCounts[conversation.id] !== flaggedCount || 
          newTotalFlaggedCounts[conversation.id] !== totalFlaggedCount) {
        newFlaggedCounts[conversation.id] = flaggedCount;
        newTotalFlaggedCounts[conversation.id] = totalFlaggedCount;
        
        if (flaggedCount > 0 && newCategories[conversation.id] === 'archive') {
          newCategories[conversation.id] = 'new';
        } else if (flaggedCount === 0 && newCategories[conversation.id] === 'new') {
          newCategories[conversation.id] = 'archive';
        }
        
        hasChanges = true;
      }
    }
    
    if (hasChanges) {
      conversationCategoryRef.current = newCategories;
      setFlaggedCounts(newFlaggedCounts);
      setTotalFlaggedCounts(newTotalFlaggedCounts);
      
      setConversations(prev => {
        const newConversations = prev.map(conversation => ({
          ...conversation,
          flaggedCount: newFlaggedCounts[conversation.id] || 0,
          totalFlaggedCount: newTotalFlaggedCounts[conversation.id] || 0
        }));
        
        newConversations.sort((a, b) => b.lastMessageTimestamp - a.lastMessageTimestamp);
        
        return newConversations;
      });
    }
  }, [connectionId, conversations, flaggedCounts, totalFlaggedCounts, loading, selectedConversationId]);

  useEffect(() => {
    const intervalId = setInterval(refreshFlaggedCounts, 60000);
    return () => clearInterval(intervalId);
  }, [refreshFlaggedCounts]);

  if (loading) {
    return (
      <div className={`space-y-2 ${className}`}>
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="cursor-pointer">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Skeleton className="h-8 w-8 rounded-full mr-2" />
                  <Skeleton className="h-5 w-32" />
                </div>
                <Skeleton className="h-5 w-10" />
              </div>
              <Skeleton className="h-4 w-3/4 mt-2" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 text-destructive ${className}`}>
        {error}
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className={`p-4 text-center text-muted-foreground ${className}`}>
        No flagged conversations found
      </div>
    );
  }

  const newConversations = conversations.filter(conv => 
    conversationCategoryRef.current[conv.id] === 'new'
  );
  
  const archivedConversations = conversations.filter(conv => 
    conversationCategoryRef.current[conv.id] === 'archive'
  );

  const renderConversationCard = (conversation: Conversation) => {
    const displayName = conversation.name === "Unknown" 
      ? extractPhoneFromJid(conversation.jid) 
      : conversation.name;
      
    return (
      <Card 
        key={conversation.id}
        className={`cursor-pointer transition-colors hover:bg-muted/50 ${
          selectedConversationId === conversation.id ? 'bg-muted border-primary' : ''
        }`}
        onClick={() => onSelectConversation(conversation)}
      >
        <CardContent className="p-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 min-w-0 max-w-[75%]">
              <Avatar className="h-8 w-8 flex-shrink-0">
                {conversation.photoUrl ? (
                  <AvatarImage src={conversation.photoUrl} alt={displayName} />
                ) : null}
                <AvatarFallback>
                  {conversation.type === 'group' ? (
                    <Users className="h-4 w-4" />
                  ) : (
                    <User className="h-4 w-4" />
                  )}
                </AvatarFallback>
              </Avatar>
              <div className="font-medium flex items-center gap-2 min-w-0">
                <span className="truncate">{displayName}</span>
                {conversation.type === 'group' ? (
                  <Users className="h-4 w-4 text-primary flex-shrink-0" />
                ) : (
                  <User className="h-4 w-4 text-primary flex-shrink-0" />
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {conversation.flaggedCount > 0 && (
                <Badge className="bg-destructive hover:bg-destructive text-destructive-foreground">
                  {conversation.flaggedCount}
                </Badge>
              )}
            </div>
          </div>
          
          {conversation.lastMessagePreview && (
            <div className="text-sm text-muted-foreground truncate mt-1 ml-10">
              {conversation.lastMessagePreview}
            </div>
          )}
          
          <div className="flex justify-between items-center mt-2 ml-10">
            <div className="text-xs text-muted-foreground">
              {conversation.lastMessageTimestamp 
                ? format(new Date(conversation.lastMessageTimestamp * 1000), 'MMM d, h:mm a') 
                : 'Unknown time'}
            </div>
            
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <MessageCircle className="h-3 w-3" />
              <span>
                {conversation.flaggedCount > 0 
                  ? `${conversation.flaggedCount}/${conversation.totalFlaggedCount || conversation.flaggedCount}`
                  : conversation.totalFlaggedCount || 0}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {newConversations.length > 0 && (
        <div>
          <div className="text-sm font-semibold text-primary mb-2 px-1">New</div>
          <div className="space-y-2">
            {newConversations.map(renderConversationCard)}
          </div>
        </div>
      )}
      
      {newConversations.length > 0 && archivedConversations.length > 0 && (
        <Separator className="my-4" />
      )}
      
      {archivedConversations.length > 0 && (
        <div>
          <div className="text-sm font-semibold text-muted-foreground mb-2 px-1">Archive</div>
          <div className="space-y-2">
            {archivedConversations.map(renderConversationCard)}
          </div>
        </div>
      )}
    </div>
  );
}
